/**
 * Subtle "Sign in to unlock personalized picks" CTA card.
 * Shown directly under the Editor's Picks rail for guest (non-signed-in) users.
 *
 * Behavior:
 *  - Renders nothing if the user is signed in.
 *  - Dismissible (✕). Remembers dismissal in localStorage for 7 days.
 *  - Shown at most once per session (and not at all if dismissed).
 */
import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { startGoogleLogin } from "../lib/googleAuth";
import GoogleSignInButton from "./GoogleSignInButton";

const DISMISS_KEY = "fm_signin_nudge_dismissed_at";
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

export default function SignInNudge() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (user) {
      setHidden(true);
      return;
    }
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (ts && Date.now() - ts < SEVEN_DAYS) {
        setHidden(true);
        return;
      }
    } catch (e) {
      // localStorage unavailable — show by default
    }
    setHidden(false);
  }, [user]);

  if (user || hidden) return null;

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) { /* */ }
    setHidden(true);
  };

  const onSignIn = async () => {
    try { await startGoogleLogin(); } catch (e) { /* startGoogleLogin already alerts */ }
  };

  return (
    <section className="px-6 lg:px-10 mt-2" data-testid="signin-nudge">
      <div className="relative max-w-[1440px] mx-auto rounded-xl border border-white/10 bg-gradient-to-r from-[#E50914]/10 via-white/[0.03] to-[#007AFF]/10 p-5 sm:p-6 overflow-hidden">
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          data-testid="signin-nudge-dismiss"
          className="absolute top-3 right-3 p-1 rounded-full text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="w-10 h-10 rounded-full bg-[#E50914]/20 border border-[#E50914]/40 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[#E50914]" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-base sm:text-lg font-bold tracking-tight text-white">
              Get picks tuned to your taste
            </h3>
            <p className="text-xs sm:text-sm text-neutral-400 mt-1 max-w-xl">
              Sign in to save your watchlist and unlock a personalized home feed that refreshes for you.
            </p>
          </div>
          <GoogleSignInButton onClick={onSignIn} variant="light" testid="signin-nudge-cta" />
        </div>
      </div>
    </section>
  );
}
